
import { useEffect, useState } from 'react';
import { ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps';
import { getCorrectCountryCode } from '../utils/completeCountryMapping';

const geoUrl = process.env.PUBLIC_URL + "/world-110m.json";

const WorldMap = ({ geoipData, onRegionSelect }) => {
    const [hoveredRegion, setHoveredRegion] = useState(null);

    // Effect to prevent body scroll when modal is open
    useEffect(() => {
        document.body.classList.add('modal-open');
        return () => {
            document.body.classList.remove('modal-open');
        };
    }, []);

    // Generate specific colors for each region
    const generateRegionColors = (regions) => {
        const regionColorMap = {
            "North America": "#01B4BD",
            "Central America / Caribbean": "#235FA8",
            "South America": "#D4C2EF",
            "Western Europe": "#096DD9",
            "Eastern Europe": "#002766",
            "West Africa": "#6224C6",
            "MENA": "#B9DBFE",
            "Central/South Asia": "#9A6EDC",
            "East/Southeast Asia": "#319EFD",
            "Oceania": "#128A8C",
            "Central/East Africa": "#B4EEEE" // Additional color for the missing region
        };

        const regionColors = {};
        regions.forEach((region) => {
            regionColors[region] = regionColorMap[region] || "#e6e6e6"; // Gray default if not mapped
        });
        return regionColors;
    };

    // Get regions and generate colors dynamically from data
    const regions = Object.keys(geoipData || {});
    const regionColors = generateRegionColors(regions);

    // Create country to region mapping dynamically from data
    const countryToRegion = {};
    Object.keys(geoipData || {}).forEach(region => {
        // Handle both array and object formats
        if (Array.isArray(geoipData[region])) {
            // Simplified format: array of country codes
            geoipData[region].forEach(countryCode => {
                countryToRegion[countryCode] = region;
            });
        } else if (typeof geoipData[region] === 'object') {
            // Complex format: object with codes as keys
            Object.keys(geoipData[region]).forEach(countryCode => {
                countryToRegion[countryCode] = region;
            });
        }
    });

    // Helper functions
    const getCountryRegion = (countryCode) => countryToRegion[countryCode] || null;
    const getRegionColor = (region) => regionColors[region] || "#e6e6e6";
    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',

        }}>

            <div style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                width: '95%',
                maxWidth: '1200px',
                height: '90%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)'
            }}>
                {/* Header */}


                {/* Map Container */}

                <div style={{
                    flex: 1,
                    position: 'relative',
                    overflow: 'hidden',
                    backgroundColor: '#f8fafc'
                }}>
                    <h1 style={{ padding: 10, paddingLeft: 30, position: 'absolute', }}>Exploring the Starlink  IP geolocation Dataset</h1>
                    <ComposableMap
                        projection="geoNaturalEarth1"
                        projectionConfig={{
                            scale: 160,
                            center: [0, 0]
                        }}
                        style={{
                            width: '100%',
                            height: '100%'
                        }}
                    >
                        <ZoomableGroup
                            zoom={1.15}
                            center={[15, 0]}
                            minZoom={1.15}
                            maxZoom={1.15}
                        >
                            <Geographies geography={geoUrl}>
                                {({ geographies }) => (
                                    <>
                                        {/* Render countries */}
                                        {geographies.map((geo) => {
                                            // Get country identifier (use name or id)
                                            const countryName = geo.properties.name;
                                            const countryId = geo.id;

                                            // Search for code using the name -> code mapping
                                            const countryCode = getCorrectCountryCode(countryName, countryId, geoipData);

                                            // Determine if the country has data and its region
                                            const region = getCountryRegion(countryCode);
                                            const hasData = region && geoipData && geoipData[region];

                                            // Get color based on region or use gray default
                                            const baseColor = hasData ? getRegionColor(region) : "#e6e6e6";

                                            // Determine if it's being hovered (countries from the same region)
                                            const isHovered = hoveredRegion === region;
                                            const isClickable = hasData;

                                            // Keep original color without brightness effect
                                            const fillColor = baseColor;

                                            return (
                                                <Geography
                                                    key={geo.rsmKey}
                                                    geography={geo}
                                                    fill={fillColor}
                                                    stroke={isHovered && hasData ? "#333333" : "#ffffff"}
                                                    strokeWidth={isHovered && hasData ? 2 : 0.5}
                                                    onMouseEnter={() => {
                                                        if (hasData) {
                                                            console.log(`🔍 Hover: ${countryName} (${countryCode}) -> Region: "${region}"`);
                                                            setHoveredRegion(region);
                                                        }
                                                    }}
                                                    onMouseLeave={() => {
                                                        setHoveredRegion(null);
                                                    }}
                                                    onClick={() => {
                                                        if (hasData && onRegionSelect) {
                                                            console.log(`🌍 Selected region: "${region}"`);
                                                            onRegionSelect(region);
                                                        }
                                                    }}
                                                    style={{
                                                        default: {
                                                            outline: "none",
                                                            cursor: isClickable ? "pointer" : "default"
                                                        },
                                                        hover: {
                                                            outline: "none",
                                                            cursor: isClickable ? "pointer" : "default"
                                                        },
                                                        pressed: {
                                                            outline: "none"
                                                        }
                                                    }}
                                                />
                                            );
                                        })}

                                        {/* Region Labels - positioned using predefined coordinates */}
                                        {regions.map((region) => {
                                            // Absolute SVG coordinates for the real system regions
                                            const regionPositions = {
                                                'North America': { x: 200, y: 200 },
                                                'Central America / Caribbean': { x: 250, y: 260 },
                                                'South America': { x: 280, y: 350 },
                                                'Western Europe': { x: 360, y: 170 },
                                                'Eastern Europe': { x: 470, y: 170 },
                                                'West Africa': { x: 420, y: 260 },
                                                'Central/East Africa': { x: 480, y: 330 },
                                                'South and Central Africa': { x: 480, y: 380 },
                                                'MENA': { x: 500, y: 240 },
                                                'Central/South Asia': { x: 580, y: 200 },
                                                'East/Southeast Asia': { x: 720, y: 300 },
                                                'Oceania': { x: 750, y: 380 }
                                            };

                                            const position = regionPositions[region];
                                            if (!position) return null;

                                            const isHighlighted = hoveredRegion === region;

                                            // Calculate text width more accurately (approximately 4.8px per character for 7.7px font)
                                            const textWidth = region.length * 4.8;
                                            const rectWidth = textWidth + 20; // 20px total margin (10px each side)
                                            const rectX = position.x - (rectWidth / 2);

                                            return (
                                                <g key={`label-${region}`}>
                                                    {/* Background rectangle for the label */}
                                                    <rect
                                                        x={rectX}
                                                        y={position.y - 6}
                                                        width={rectWidth}
                                                        height={12}
                                                        fill={isHighlighted ? "rgba(255, 255, 255, 1)" : "rgba(255, 255, 255, 0.7)"}
                                                        stroke="#d1d5db"
                                                        strokeWidth="0.5"
                                                        rx="2"
                                                        ry="2"
                                                        style={{
                                                            transition: 'all 0.2s ease',
                                                            cursor: 'pointer'
                                                        }}
                                                        onClick={() => {
                                                            if (onRegionSelect) {
                                                                console.log(`🏷️ Region label clicked: "${region}"`);
                                                                onRegionSelect(region);
                                                            }
                                                        }}
                                                        onMouseEnter={() => setHoveredRegion(region)}
                                                        onMouseLeave={() => setHoveredRegion(null)}
                                                    />
                                                    <text
                                                        x={position.x}
                                                        y={position.y + 1}
                                                        textAnchor="middle"
                                                        dominantBaseline="middle"
                                                        style={{
                                                            fontSize: '7.7px',
                                                            fontWeight: '700',
                                                            fill: isHighlighted ? '#FA541C' : '#000000',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease',
                                                            fontFamily: 'Roboto Flex, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                                            pointerEvents: 'none'
                                                        }}
                                                    >
                                                        {region}
                                                    </text>
                                                </g>
                                            );
                                        })}
                                    </>
                                )}
                            </Geographies>
                        </ZoomableGroup>
                    </ComposableMap>

                </div>
                { /*
                <div style={{ position: 'relative' }}>
                    <div style={{ position: 'absolute', width: 256, right: 26, bottom: 26, zIndex: 1000 }}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 227 45.86" width="auto" height="auto" aria-label="Kentik"><path fill="#17181B" d="M83.42 16.88A12.71 12.71 0 0 0 81 15.36a6.83 6.83 0 0 0-2.88-.59 4.89 4.89 0 0 0-2.64.68 9.85 9.85 0 0 0-2.14 1.93l-6.58 7.51A7 7 0 0 0 65.12 28V5.77a5.5 5.5 0 0 0-.49-2.51 4.24 4.24 0 0 0-1.31-1.52 4.61 4.61 0 0 0-1.8-.74 10.22 10.22 0 0 0-2-.19 15.59 15.59 0 0 0-1.86.1 5.61 5.61 0 0 0-1 .15V44.8h8.44V29.85A6.25 6.25 0 0 0 66.49 33l6.88 9.24A7.39 7.39 0 0 0 80 45.42a8.58 8.58 0 0 0 2.74-.42 7.69 7.69 0 0 0 2.17-1l-11.6-15.46Zm25.66 1.71a10.89 10.89 0 0 0-3.94-2.92 14.41 14.41 0 0 0-5.89-1.09 16.29 16.29 0 0 0-5.22.87 12.85 12.85 0 0 0-4.56 2.73 13.6 13.6 0 0 0-3.22 4.69A17 17 0 0 0 85 29.6a19.9 19.9 0 0 0 1.37 7.91 13.36 13.36 0 0 0 3.57 5 12.75 12.75 0 0 0 5 2.61 22.58 22.58 0 0 0 5.73.75 19.5 19.5 0 0 0 5.61-.72 11.58 11.58 0 0 0 3.69-1.7 3.13 3.13 0 0 0 1.62-2.45 5.28 5.28 0 0 0-.81-2.39 17.93 17.93 0 0 0-1.55-2.38L108 37.26a8.47 8.47 0 0 1-1.62 1.09 10.33 10.33 0 0 1-2.1.83 9.78 9.78 0 0 1-2.73.35c-2.69 0-4.66-.76-5.89-2.27a9.78 9.78 0 0 1-2.11-5.68h18.23a4.81 4.81 0 0 0 .1-.83l.06-1.09v-1.92a18 18 0 0 0-.68-5 11.57 11.57 0 0 0-2.18-4.15Zm-15.61 8.34A7.44 7.44 0 0 1 95 22a5.08 5.08 0 0 1 4.1-1.77c2 0 3.36.62 4.12 1.86a9.08 9.08 0 0 1 1.15 4.84Zm49.88-6.02a7.91 7.91 0 0 0-2.73-4.59c-1.4-1.15-3.57-1.74-6.51-1.74a12.09 12.09 0 0 0-5.89 1.22 9.84 9.84 0 0 0-3.22 2.63 3.37 3.37 0 0 0-1.84-2.51 7.76 7.76 0 0 0-3.44-.71h-1.17q-.5 0-.87.09l-.75.12V44.8h8.5V24.7a2.78 2.78 0 0 1 1.65-2.39 7.66 7.66 0 0 1 3.93-1 5.58 5.58 0 0 1 2.52.46 2.84 2.84 0 0 1 1.3 1.31 5.75 5.75 0 0 1 .5 2c.06.79.09 1.64.09 2.54V44.8h8.44V27.3c0-1.07 0-2.17-.09-3.29a20.58 20.58 0 0 0-.42-3.1ZM167.05 37c-.12.13-.33.31-.62.56a5.46 5.46 0 0 1-1.08.71 11.82 11.82 0 0 1-1.43.63 5 5 0 0 1-1.65.28q-3.53 0-3.53-4.34V21.59h8.68v-6.32h-8.68v-4.34a5.53 5.53 0 0 0-.5-2.52 4.13 4.13 0 0 0-1.3-1.52 4.61 4.61 0 0 0-1.8-.74 10.32 10.32 0 0 0-2-.19h-1c-.33 0-.64.06-.93.1l-.93.12V35q0 1.36.12 2.73a10.66 10.66 0 0 0 .56 2.6 7.93 7.93 0 0 0 3 3.91 10.35 10.35 0 0 0 6 1.49 15.61 15.61 0 0 0 3.66-.4 11.1 11.1 0 0 0 2.85-1.06 6.32 6.32 0 0 0 1.83-1.42 2.44 2.44 0 0 0 .65-1.59 4.17 4.17 0 0 0-.49-1.86 21.24 21.24 0 0 0-1.41-2.4Zm12.22-20.89a4.9 4.9 0 0 0-1.8-.72 11 11 0 0 0-2-.18 17.83 17.83 0 0 0-1.86.09 5.43 5.43 0 0 0-1 .15v24.82c0 2.07.51 3.44 1.52 4.12a6.89 6.89 0 0 0 3.94 1c.66 0 1.29 0 1.86-.06a3.43 3.43 0 0 0 1.12-.19V20.11a5.46 5.46 0 0 0-.5-2.51 3.81 3.81 0 0 0-1.28-1.49Zm25.13 12.43 10.11-11.66a12.71 12.71 0 0 0-2.45-1.52 6.84 6.84 0 0 0-2.89-.59 4.88 4.88 0 0 0-2.63.68 9.63 9.63 0 0 0-2.14 1.93l-6.58 7.51a6.87 6.87 0 0 0-1.61 3.1V5.77a5.49 5.49 0 0 0-.5-2.51 4.21 4.21 0 0 0-1.3-1.52 4.61 4.61 0 0 0-1.8-.74 10.24 10.24 0 0 0-2-.19 15.59 15.59 0 0 0-1.86.1 5.41 5.41 0 0 0-1 .15V44.8h8.44V29.85a6.25 6.25 0 0 0 1.37 3.1l6.88 9.24a7.39 7.39 0 0 0 6.64 3.23 8.47 8.47 0 0 0 2.72-.44 7.76 7.76 0 0 0 2.18-1ZM176.61 1a4.48 4.48 0 0 0-3.23 1.27 4.35 4.35 0 0 0-1.31 3.26 4.23 4.23 0 0 0 1.31 3.22 4.77 4.77 0 0 0 6.42 0 4.18 4.18 0 0 0 1.33-3.22 4.3 4.3 0 0 0-1.33-3.26A4.51 4.51 0 0 0 176.61 1Z"></path><path fill="#e05420" d="M41.08 19.89a4.51 4.51 0 0 0-3.19-1.27 4.45 4.45 0 0 0-3.23 1.27 4.35 4.35 0 0 0-1.31 3.26 4.23 4.23 0 0 0 1.31 3.22 4.47 4.47 0 0 0 3.23 1.24 4.52 4.52 0 0 0 3.19-1.24 4.18 4.18 0 0 0 1.33-3.22 4.3 4.3 0 0 0-1.33-3.26Z"></path><path fill="#e05420" d="M24.91 23.12 40.49 2.3a8.28 8.28 0 0 0-2.17-1 8.49 8.49 0 0 0-2.73-.49 8.65 8.65 0 0 0-3.72.72A8.66 8.66 0 0 0 29 4L14.78 23.12 29 42.19a8.55 8.55 0 0 0 2.91 2.51 8.65 8.65 0 0 0 3.72.72 8.53 8.53 0 0 0 2.69-.42 8.28 8.28 0 0 0 2.17-1Z"></path><path fill="#e05420" d="M25.71 2.3a8.28 8.28 0 0 0-2.17-1 8.58 8.58 0 0 0-2.73-.49 8.65 8.65 0 0 0-3.72.72A8.78 8.78 0 0 0 14.17 4L0 23.12l14.17 19.07a8.57 8.57 0 0 0 2.92 2.51 8.65 8.65 0 0 0 3.72.72 8.58 8.58 0 0 0 2.73-.42 8.28 8.28 0 0 0 2.17-1L10.13 23.12Z"></path><path fill="#17181B" d="M222.41 45.47a4.27 4.27 0 1 1 0-8.54 4.27 4.27 0 0 1 0 8.54Zm0-8a3.76 3.76 0 1 0 2.66 1.1 3.63 3.63 0 0 0-2.66-1.14Zm2.07 6.44h-.92l-.56-1.35c-.3-.65-.62-1-1-1h-.5v2.29h-.76v-5.41h1.53a2.37 2.37 0 0 1 1.46.39 1.3 1.3 0 0 1 .52 1.09 1.37 1.37 0 0 1-.36.95 1.92 1.92 0 0 1-1 .53c.3.06.6.4.89 1Zm-3-4.79v1.86h.81c.75 0 1.13-.31 1.13-.93a.83.83 0 0 0-.31-.72 1.69 1.69 0 0 0-.95-.21Z"></path></svg>
                    </div>
                </div>
                */}

            </div>
        </div >
    );
};

export default WorldMap;
